/**
 * Finding Your Voice — Toolkit
 *
 * Practical tools drawn from the programme modules.
 * Each tool is associated with a guide character and links to related modules.
 */

/**
 * Component mapping for interactive toolkit tools.
 * Keys match tool IDs; values are the component names (lazy-loaded in ToolDetailPage).
 */
export const TOOL_COMPONENTS = {
  'fear-companion': 'FearCompanion',
  'ground-finder': 'GroundFinder',
  'message-distillery': 'MessageDistillery',
  'audience-portrait': 'AudiencePortrait',
  'feeling-compass': 'FeelingCompass',
  'presence-prep': 'PresencePrep',
  'message-architect': 'MessageArchitect',
  'courtyard-journal': 'CourtyardJournal',
  'complete-speaking-prep': 'CompleteSpeakingPrep',
  'quick-reset': 'QuickReset',
};

export const TOOLS = [
  {
    id: 'fear-companion',
    component: 'FearCompanion',
    name: 'Fear Companion',
    character: 'philemon',
    icon: 'Heart',
    description: 'A guided way to sit with one fear at a time instead of fighting it, the way Philemon does.',
    intro: {
      characterId: 'philemon',
      text: 'Sit with me a moment. Before you fight the fear, we\'re going to listen to it. It has been trying to tell you what matters.',
    },
    relatedModules: [1, 5],
    steps: [
      {
        title: 'Name the Fear',
        instruction: 'Close your eyes. Ask yourself: what am I actually afraid of right now? Be specific: not "messing up" but "forgetting my opening line" or "being judged by the person in the third row." Name it precisely.',
      },
      {
        title: 'Locate It in Your Body',
        instruction: 'Where does this fear live in your body? Tight chest? Knotted stomach? Tingling hands? Don\'t try to change the sensation. Just notice it. Put your attention on that spot and breathe into it.',
      },
      {
        title: 'Ask Whose Voice It Is',
        instruction: 'The harshest judge is often one you\'ve carried in: an old teacher, a mocking cousin, a younger you. Whose voice does this fear speak in? Name them, and notice whether they will actually be in the room.',
      },
      {
        title: 'Ask What It\'s Protecting',
        instruction: 'A fear about speaking is usually guarding something you care about. Ask: what does this fear tell me I value? Your reputation? Being understood? Connection with this audience? The answer is your fuel.',
      },
      {
        title: 'Thank It and Proceed',
        instruction: 'Silently say: "Thank you. I hear you. I\'m going to do this anyway." Then take three slow breaths and begin. The fear may stay. That\'s fine. It just doesn\'t get to make the decisions.',
      },
    ],
  },
  {
    id: 'ground-finder',
    component: 'GroundFinder',
    name: 'Ground Finder',
    character: 'philemon',
    icon: 'Anchor',
    description: 'Three questions to find your ground before speaking. Drawn from Xenia\'s teaching about authentic confidence having roots.',
    intro: {
      characterId: 'philemon',
      text: 'Three questions before you speak. Xenia taught them to me, and I return to them whenever the ground feels thin.',
    },
    relatedModules: [2],
    steps: [
      {
        title: 'What Do I Know?',
        instruction: 'Write down what you know about this topic: lived experience as well as credentials. What have you seen, done, survived, or learned that gives you the right to speak? This is your knowledge ground.',
      },
      {
        title: 'What Do I Believe?',
        instruction: 'What do you actually believe about this subject? Set aside what\'s fashionable or expected and state your own conviction plainly, even if it\'s uncomfortable. This is your values ground.',
      },
      {
        title: 'Why Does This Matter?',
        instruction: 'Why are you the one saying this? What\'s at stake for you personally? Knowing what is at stake for you gives you a reason to speak that no technique can supply. This is your purpose ground.',
      },
    ],
  },
  {
    id: 'message-distillery',
    component: 'MessageDistillery',
    name: 'Message Distillery',
    character: 'theron',
    icon: 'Filter',
    description: 'Reduce any message to its essential core. Theron\'s process for achieving ruthless clarity.',
    intro: {
      characterId: 'theron',
      text: 'A harvest, a ruthless cut, one clear sentence. This is how cargo becomes a voyage. Bring everything you might say. We\'ll leave most of it on the dock.',
    },
    relatedModules: [3],
    steps: [
      {
        title: 'The Harvest',
        instruction: 'Write down everything you could possibly say about this topic. Every point, every tangent, every "oh and also." Don\'t filter. Just capture. Aim for at least a full page.',
      },
      {
        title: 'The Cut',
        instruction: 'Now cross out everything that isn\'t essential. Be ruthless. "Interesting" is not the same as "necessary." If a point doesn\'t directly serve your core message, it goes. Aim to cut at least 60 per cent.',
      },
      {
        title: 'The Sentence',
        instruction: 'State what remains in one sentence. Not two. Not a run-on sentence with semicolons. One clear, direct sentence. If you can\'t do it, go back to The Cut. You haven\'t been ruthless enough.',
      },
      {
        title: 'Know · Feel · Do',
        instruction: 'Test your sentence against three questions, one line each: What should they know? What should they feel? What should they do? A vague answer shows you exactly where the message still needs work.',
      },
      {
        title: 'Three Anchor Words',
        instruction: 'Distil the sentence into three words that point to the message. Write them where you can see them. When your mind goes white, the next word tells you where you are.',
      },
    ],
  },
  {
    id: 'audience-portrait',
    component: 'AudiencePortrait',
    name: 'Audience Portrait',
    character: 'xenia',
    icon: 'Users',
    description: 'Build an emotional profile of who you\'re speaking to. Xenia\'s approach to shifting focus from self to other.',
    intro: {
      characterId: 'xenia',
      text: 'Before you think about what you\'ll say, let us think about who is listening. Rooms open for speakers who arrive already curious about them.',
    },
    relatedModules: [4],
    steps: [
      {
        title: 'Who Are They?',
        instruction: 'Before thinking about what you\'ll say, think about who you\'re saying it to, and about how they feel rather than who they are on paper. What are they dealing with today? What brought them to this room? What\'s on their mind?',
      },
      {
        title: 'What Do They Already Know?',
        instruction: 'Assess their existing knowledge honestly. Where are the gaps? What assumptions might they bring? What jargon will confuse them? Meet them where they are, not where you wish they were.',
      },
      {
        title: 'What Do They Need?',
        instruction: 'Information? Reassurance? Direction? Permission to act? Comfort? Challenge? The answer shapes how you deliver your message.',
      },
      {
        title: 'What Are They Afraid Of?',
        instruction: 'Most people in a room are worried about something. If you can name that worry, even silently, you are more likely to speak in a way that makes them feel understood.',
      },
      {
        title: 'The Unspoken Questions',
        instruction: 'Every audience carries silent questions: Why should I care? Can I trust you? What would you have me do? Mark where in your message each is answered. An unanswered one is a hole the audience quietly falls into.',
      },
      {
        title: 'How Will You Know It Landed?',
        instruction: 'Define success from the audience\'s perspective. What will they do, feel, or understand differently? This is your true measure: what shifted for them, not how you sounded.',
      },
    ],
  },
  {
    id: 'feeling-compass',
    component: 'FeelingCompass',
    name: 'Feeling Compass',
    character: 'philemon',
    icon: 'Compass',
    description: 'Identify what you\'re feeling and what it\'s telling you. Philemon\'s practice for using emotion as information rather than hiding it.',
    intro: {
      characterId: 'philemon',
      text: 'What are you feeling, right now, before you speak? Don\'t tidy it. The feeling is information. Let us find out what it\'s pointing at.',
    },
    relatedModules: [5, 1],
    steps: [
      {
        title: 'Pause and Notice',
        instruction: 'Before you speak, pause for five seconds. In that pause, turn your attention inward. Don\'t analyse. Just notice. What is the weather inside you right now?',
      },
      {
        title: 'Name It Precisely',
        instruction: 'Use specific emotional words: anxious, excited, frustrated, hopeful, tender, angry, curious, sad, determined. "Fine" and "okay" are not emotions. Go deeper.',
      },
      {
        title: 'Find the Message',
        instruction: 'Ask: what is this feeling telling me? Often anxiety says: this matters. Anger says: something is wrong. Sadness says: something has been lost. Excitement says: something is possible. The feeling has information. Listen.',
      },
      {
        title: 'Let It Through',
        instruction: 'Speak without suppressing the feeling. You don\'t need to announce it or perform it. Just stop hiding it, and let your voice carry what you feel. Hiding a feeling takes effort, and that effort comes out of what you have left for thinking and connecting.',
      },
    ],
  },
  {
    id: 'presence-prep',
    component: 'PresencePrep',
    name: 'Presence Prep',
    character: 'theron',
    icon: 'Mountain',
    description: 'A physical preparation ritual for just before you speak. Theron\'s sixty-second grounding practice.',
    intro: {
      characterId: 'theron',
      text: 'Feet, shoulders, jaw, breath. It takes less time than pouring a round of wine, and you will stand differently for it.',
    },
    relatedModules: [6],
    steps: [
      {
        title: 'Feel Your Feet',
        instruction: 'Stand still. Feel both feet on the ground: the contact points, the weight distribution, the floor holding you up. You are here. You are solid. The earth has you.',
      },
      {
        title: 'Drop Your Shoulders',
        instruction: 'Let your shoulders fall away from your ears. Unclench your jaw. Soften your forehead. Release every bit of tension you can find. You don\'t need to carry it right now.',
      },
      {
        title: 'Three Deep Breaths',
        instruction: 'In through the nose, slow. Out through the mouth, slower. Three times. On each exhale, let go of a little more tension. Feel your centre of gravity drop from your chest to your belly.',
      },
      {
        title: 'Open and See',
        instruction: 'On the last exhale, open your eyes (if they were closed) and look at the room. Not scanning. Seeing. Notice the people, the space, the light. You are present. Now begin.',
      },
    ],
  },
  {
    id: 'message-architect',
    component: 'MessageArchitect',
    name: 'Message Architect',
    character: 'theron',
    icon: 'Layout',
    description: 'Structure a message on one of four simple frameworks. Theron\'s approach to building a crate that carries your ideas across whole.',
    intro: {
      characterId: 'theron',
      text: 'Three words on the back of my hand have carried me through every port on the Middle Sea. Let us find yours, and craft the doors you\'ll enter and leave by.',
    },
    relatedModules: [7, 3],
    steps: [
      {
        title: 'Choose Your Framework',
        instruction: 'Select the framework that best suits your message. The List: three points side by side. Problem–Solution: problem, solution, benefit, for persuasion. The Timeline: past, present, future, for updates and change. Question–Answer: what, so what, now what, for briefings. When in doubt, use Question–Answer.',
      },
      {
        title: 'Find Your Three Anchors',
        instruction: 'Distil your message into exactly three words, one for each section of your framework. These are your anchors. They don\'t contain the message; they point to it. Write them somewhere visible.',
      },
      {
        title: 'Build Your Transitions',
        instruction: 'Write explicit transition sentences between each section. "That\'s the problem. Here\'s what I propose." "I\'ve told you where we were. Now here\'s where we\'re going." Transitions are the bridges your audience walks across.',
      },
      {
        title: 'Craft the Doors',
        instruction: 'Write your first and last sentences word for word. People remember the beginning and the end of a sequence best, so these two are worth getting exactly right. No throat-clearing at the door in: your first breath should earn your second. And never improvise the door out.',
      },
      {
        title: 'Preview and Practise',
        instruction: 'Read the whole structure once, then deliver it from the anchors and doors alone. No script beyond those words. Trust your knowledge to fill the middle. If you get lost, the next anchor tells you where you are.',
      },
    ],
  },
  {
    id: 'courtyard-journal',
    component: 'CourtyardJournal',
    name: 'Courtyard Journal',
    character: 'xenia',
    icon: 'BookOpen',
    description: 'Reflection prompts for ongoing practice. Xenia\'s approach to learning from every speaking experience, brilliant or disastrous.',
    intro: {
      characterId: 'xenia',
      text: 'Every speaking moment leaves something behind, if you stop to gather it. Sit. Tell me what happened, and we\'ll find what it taught you.',
    },
    relatedModules: [8],
    steps: [
      {
        title: 'What Happened?',
        instruction: 'After a speaking experience, write a brief account of what actually happened. Leave aside how you felt about it and record what occurred: who was there, what you said, how the room responded.',
      },
      {
        title: 'What Went Well?',
        instruction: 'Be specific. Not "it was okay" but "I made eye contact with the person in the back row and saw them nod" or "I paused after my key point and let the silence sit." Notice the small victories.',
      },
      {
        title: 'What Surprised You?',
        instruction: 'Speaking usually brings a surprise or two: a question you didn\'t expect, a moment of connection you didn\'t plan, your own emotion catching you off guard. Name the surprises.',
      },
      {
        title: 'What Would You Do Differently?',
        instruction: 'This is for learning, not self-criticism. If you could do it again with the benefit of hindsight, what one thing would you change? That one thing is where you grow next.',
      },
      {
        title: 'What Did You Learn?',
        instruction: 'Each speaking experience can teach you something about yourself as a communicator. Name it. Write it down. Over weeks and months the entries add up to a knowledge of yourself that theory cannot give you.',
      },
    ],
  },
  {
    id: 'complete-speaking-prep',
    component: 'CompleteSpeakingPrep',
    name: 'Complete Speaking Prep',
    character: 'all',
    icon: 'CheckSquare',
    description: 'The full preparation sequence, drawing on all three guides and all eight evenings. Eight checks for any speaking occasion that matters.',
    intro: {
      characterId: 'xenia',
      text: 'Everything the courtyard teaches, gathered into one preparation. Philemon will steady you, Theron will sharpen you, and I will turn you towards your audience.',
    },
    relatedModules: [1, 2, 3, 4, 5, 6, 7, 8],
    steps: [
      {
        title: 'Fear Check (Philemon)',
        instruction: 'What am I afraid of? What is the fear telling me about what matters here? Acknowledge the fear, thank it, and proceed.',
      },
      {
        title: 'Ground Check (Philemon)',
        instruction: 'What do I know about this? What do I believe? Why does this matter to me? Find your ground.',
      },
      {
        title: 'Clarity Check (Theron)',
        instruction: 'What is my one-sentence message? If I can\'t state it clearly, I\'m not ready. Distil until I can.',
      },
      {
        title: 'Audience Check (Xenia)',
        instruction: 'Who am I speaking to? What do they already know? What do they need? What are they afraid of?',
      },
      {
        title: 'Emotion Check (Philemon)',
        instruction: 'What am I feeling right now? Can I let it through rather than suppressing it? My emotion is information.',
      },
      {
        title: 'Body Check (Theron)',
        instruction: 'Feet on the ground. Shoulders down. Jaw unclenched. Three breaths. Open and see the room.',
      },
      {
        title: 'Structure Check (Theron)',
        instruction: 'What are my three anchor words? What framework am I using? What are my transitions?',
      },
      {
        title: 'The Threshold (All Three)',
        instruction: 'You are prepared. The last step is willingness. Take the breath. Look at the room. Begin.',
      },
    ],
  },
  {
    id: 'quick-reset',
    component: 'QuickReset',
    name: 'Quick Reset',
    character: 'all',
    icon: 'RefreshCw',
    description: 'A grounding exercise that takes about two minutes. When anxiety spikes mid-conversation or just before you speak, use this to reset.',
    intro: {
      characterId: 'theron',
      text: 'When the moment wobbles, mid-speech, mid-meeting, mid-sentence, you don\'t need an hour. You need two minutes and somewhere to put your feet.',
    },
    relatedModules: [1, 6],
    steps: [
      {
        title: 'Stop (10 seconds)',
        instruction: 'Whatever you\'re doing, pause. You are allowed to take a moment. Silence is a choice, not a failure. If you\'re mid-sentence, finish it and stop.',
      },
      {
        title: 'Ground (20 seconds)',
        instruction: 'Feel your feet. Feel the chair if you\'re sitting. Press your fingertips together firmly for three seconds, then release. This gives your body something physical to focus on.',
      },
      {
        title: 'Breathe (40 seconds)',
        instruction: 'Four breaths. In through the nose for four counts. Out through the mouth for six counts. Slow breathing with a long out-breath is linked with a calmer body.',
      },
      {
        title: 'Refocus (30 seconds)',
        instruction: 'Shift your attention outward. Look at one person in the room. What might they be feeling? Choose the kindest plausible explanation for whatever you see, and let curiosity about them replace anxiety about yourself.',
      },
      {
        title: 'Continue (30 seconds)',
        instruction: 'Return to what you were saying. If you\'ve lost your thread, say so: "Let me come back to the point I was making." Being honest about losing your place is better than pretending it didn\'t happen.',
      },
    ],
  },
];
